import { faCheck, faEllipsis, faReply, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Chip from '@mui/material/Chip';
import styled from 'styled-components';
import React from 'react';

import {
    activeStatus,
    cancelStatus,
    invoiceStatusCancelled,
    invoiceStatusPaid,
    invoiceStatusRefunded,
} from './constant';

const Div = styled.div`
    .status-badge {
        font-family: 'Poppins', sans-serif !important;
        font-weight: 500 !important;
        font-size: 12px;
        height: 22px !important;
        min-height: 22px !important;
        max-height: 22px !important;
        padding: 0 !important;
    }

    /* Booked Status */
    .booked {
        background-color: rgba(117, 189, 47, 0.1) !important;
        color: rgba(117, 189, 47, 1) !important;
    }

    .booked .fa-check {
        color: rgba(117, 189, 47, 1) !important;
    }

    /* Cancelled Statuses */
    .cancelled {
        background-color: rgba(178, 17, 17, 1) !important;
        color: white !important;
    }

    .cancelled .fa-xmark {
        color: white !important;
    }

    /* Pending */
    .pending {
        background-color: #F5F1ED !important;
        color: #44403C !important;
    }

    .pending .fa-ellipsis {
        color: #79716B !important;
    }

    /* Unpaid - same style as pending */
    .unpaid {
        background-color: #F5F1ED !important;
        color: #44403C !important;
    }

    .unpaid .fa-ellipsis {
        color: #79716B !important;
    }

    /* In Review Status */
    .in-review {
        background-color: rgba(20, 172, 198, 0.1) !important;
        color: rgba(20, 172, 198, 1) !important;
    }

    .in-review .fa-ellipsis {
        color: rgba(20, 172, 198, 1) !important;
    }

    .MuiChip-label {
        padding: 0 12px;
    }

    .MuiChip-icon {
        margin-left: 6px !important;
        margin-right: 0;
    }

    /* Add padding to the icon side of all badges */
    .booked,
    .cancelled,
    .pending,
    .unpaid,
    .in-review {
        padding-left: 6px !important;
    }
`;

function capitalizeFirstLetter(string) {
    if (!string) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
}

const StatusBadge = ({ status, badgeName }) => {
    const statusName = badgeName ? badgeName : status;
    const lowerStatus = status?.toLowerCase();

    if (lowerStatus === invoiceStatusPaid?.toLowerCase() || lowerStatus === activeStatus?.toLowerCase()) {
        return (
            <Div>
                <Chip
                    icon={<FontAwesomeIcon icon={faCheck} className="fa-check" />}
                    label={capitalizeFirstLetter(statusName)}
                    className="booked status-badge"
                />
            </Div>
        );
    } else if (lowerStatus === invoiceStatusRefunded?.toLowerCase()) {
        return (
            <Div>
                <Chip icon={<FontAwesomeIcon icon={faReply} />} label={capitalizeFirstLetter(statusName)} className="status-badge" />
            </Div>
        );
    }
    // Cancelled, Cancelled_Patient, Cancelled_Provider
    else if (
        lowerStatus === invoiceStatusCancelled?.toLowerCase() ||
        lowerStatus === cancelStatus?.toLowerCase() ||
        lowerStatus === 'cancelled_patient' ||
        lowerStatus === 'cancelled_provider'
    ) {
        return (
            <Div>
                <Chip
                    icon={<FontAwesomeIcon icon={faXmark} className="fa-xmark" />}
                    label={capitalizeFirstLetter(statusName)}
                    className="cancelled status-badge"
                />
            </Div>
        );
    }
    // In Review
    else if (lowerStatus === 'in review') {
        return (
            <Div>
                <Chip
                    className="in-review status-badge"
                    icon={<FontAwesomeIcon icon={faEllipsis} />}
                    label={capitalizeFirstLetter(statusName)}
                />
            </Div>
        );
    }
    // Unpaid - same style as pending
    else if (lowerStatus === 'unpaid') {
        return (
            <Div>
                <Chip
                    className="unpaid status-badge"
                    icon={<FontAwesomeIcon icon={faEllipsis} className="fa-ellipsis" />}
                    label={capitalizeFirstLetter(statusName === 'unpaid' ? 'Unpaid' : statusName)}
                />
            </Div>
        );
    }
    // Pending (includes draft status and other fallbacks)
    return (
        <Div>
            <Chip
                className="pending status-badge"
                icon={<FontAwesomeIcon icon={faEllipsis} />}
                label={capitalizeFirstLetter(statusName)}
            />
        </Div>
    );
};

// Expose a helper color for simple status bars (e.g., Month view divider)
export function getStatusBarColor(status) {
    const s = (status || '').toLowerCase();
    if (s === 'booked' || s === 'active') {
        return 'rgba(117, 189, 47, 1)'; // green
    }
    if (s.includes('cancelled')) {
        return 'rgba(180, 35, 24, 1)'; // red
    }
    if (s === 'in review' || s === 'pending') {
        return 'rgba(20, 172, 198, 1)'; // blue for in-review/pending visual
    }
    if (s === 'past') {
        // Keep draft/pending gray styling for Past
        return '#79716B';
    }
    // Default to draft/pending gray
    return '#79716B';
}

export default StatusBadge;
